import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Save, Trash2, UserPlus } from 'lucide-react';
import { API_URL } from '../config';

const WORKSHOP_LOCATIONS = ['Мамбетова 325а', 'Маметовой, 3а к5'];

const emptyForm = {
  username: '',
  email: '',
  password: '',
  role: '',
  phone: '',
  workshopLocation: '',
  permissions: [],
};

const parsePermissions = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const UserEditorPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isCreating = !userId;
  const [form, setForm] = useState(emptyForm);
  const [availablePages, setAvailablePages] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        setError('');
        const requests = [
          fetch(`${API_URL}/api/pages/active`, { headers }),
          fetch(`${API_URL}/api/roles`, { headers }),
        ];
        if (!isCreating) requests.push(fetch(`${API_URL}/api/users/${userId}`, { headers }));

        const responses = await Promise.all(requests);
        if (responses.some(response => !response.ok)) {
          throw new Error('Не удалось загрузить данные редактора');
        }

        const pages = await responses[0].json();
        const rolesData = await responses[1].json();
        setAvailablePages(pages);
        setRoles(rolesData);

        if (!isCreating) {
          const user = await responses[2].json();
          setForm({
            username: user.username || '',
            email: user.email || '',
            password: '',
            role: user.role || '',
            phone: user.phone || '',
            workshopLocation: user.workshopLocation || '',
            permissions: parsePermissions(user.permissions),
          });
        }
      } catch (err) {
        setError(err.message || 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isCreating, userId]);

  const allPages = useMemo(() => Object.values(availablePages).flat(), [availablePages]);

  const request = async (url, options) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options?.headers,
      },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Не удалось сохранить изменения');
    }
    return response.json().catch(() => ({}));
  };

  const handleRoleChange = (roleName) => {
    if (!isCreating) {
      setForm(current => ({ ...current, role: roleName }));
      return;
    }

    const role = roles.find(item => item.roleName === roleName);
    const permissions = role?.permissions?.length
      ? parsePermissions(role.permissions)
      : ['admin', 'adminb24'].includes(roleName)
        ? allPages.map(page => page.id)
        : ['search-b24'];
    setForm(current => ({ ...current, role: roleName, permissions }));
  };

  const togglePermission = (permissionId) => {
    setForm(current => ({
      ...current,
      permissions: current.permissions.includes(permissionId)
        ? current.permissions.filter(id => id !== permissionId)
        : [...current.permissions, permissionId],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (!form.username.trim() || !form.role) {
        throw new Error('Заполните имя пользователя и роль');
      }
      if ((isCreating || form.password) && (form.password.length < 8 || form.password.length > 128)) {
        throw new Error('Пароль должен содержать от 8 до 128 символов');
      }

      if (isCreating) {
        await request('/api/users', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        navigate('/users', { replace: true });
        return;
      }

      await request(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({
          username: form.username,
          email: form.email,
          phone: form.phone,
          role: form.role,
          workshopLocation: form.workshopLocation,
        }),
      });
      await request(`/api/users/${userId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: form.permissions }),
      });
      if (form.password) {
        await request(`/api/users/${userId}/password`, {
          method: 'PUT',
          body: JSON.stringify({ password: form.password }),
        });
        setForm(current => ({ ...current, password: '' }));
        setShowPassword(false);
      }
      setSuccess('Изменения сохранены');
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Удалить пользователя «${form.username}»? Это действие нельзя отменить.`)) return;
    setSaving(true);
    setError('');
    try {
      await request(`/api/users/${userId}`, { method: 'DELETE' });
      navigate('/users', { replace: true });
    } catch (err) {
      setError(err.message || 'Ошибка удаления');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[320px] items-center justify-center text-sm text-gray-500">Загрузка редактора...</div>;
  }

  return (
    <div className="min-h-full bg-gray-50 p-2 sm:p-3 md:p-4">
      <form onSubmit={handleSubmit} className="mx-auto w-full max-w-6xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between md:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/users')}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              title="Назад к пользователям"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isCreating ? 'Новый пользователь' : form.username}
              </h1>
              <p className="text-sm text-gray-500">
                {isCreating ? 'Создание учётной записи и настройка доступа' : 'Данные пользователя и разрешения доступа'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isCreating && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" /> Удалить
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isCreating ? <UserPlus className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saving ? 'Сохранение...' : isCreating ? 'Создать' : 'Сохранить'}
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6">
          {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          {success && <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(300px,0.8fr)_minmax(420px,1.2fr)]">
            <section className="space-y-4">
              <h2 className="border-b border-gray-100 pb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Основные данные</h2>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">Роль</span>
                <select value={form.role} onChange={event => handleRoleChange(event.target.value)} required className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100">
                  <option value="">Выберите роль</option>
                  {roles.map(role => <option key={role.id || role.roleName} value={role.roleName}>{role.displayName || role.roleName}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">Имя пользователя</span>
                <input type="text" value={form.username} onChange={event => setForm(current => ({ ...current, username: event.target.value }))} required className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">Телефон</span>
                <input type="tel" value={form.phone} onChange={event => setForm(current => ({ ...current, phone: event.target.value }))} placeholder="+7 (___) ___-__-__" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">Email</span>
                <input type="email" value={form.email} onChange={event => setForm(current => ({ ...current, email: event.target.value }))} placeholder="name@example.com" autoComplete="email" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                <span className="mt-1.5 block text-xs text-gray-500">Для входа можно использовать имя, email или телефон.</span>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">{isCreating ? 'Пароль' : 'Новый пароль'}</span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={event => setForm(current => ({ ...current, password: event.target.value }))}
                    required={isCreating}
                    minLength={8}
                    maxLength={128}
                    autoComplete="new-password"
                    placeholder={isCreating ? 'Минимум 8 символов' : 'Оставьте пустым, чтобы не менять'}
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-3 pr-11 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(value => !value)}
                    className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
                    title={showPassword ? 'Скрыть новый пароль' : 'Показать новый пароль'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {!isCreating && <span className="mt-1.5 block text-xs text-gray-500">Сохранённый пароль не отображается. Введите новый только для его замены.</span>}
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-gray-700">Местоположение цеха</span>
                <select value={form.workshopLocation} onChange={event => setForm(current => ({ ...current, workshopLocation: event.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100">
                  <option value="">Не выбрано</option>
                  {WORKSHOP_LOCATIONS.map(location => <option key={location} value={location}>{location}</option>)}
                </select>
              </label>
            </section>

            <section>
              <div className="mb-2 flex items-center justify-between border-b border-gray-100 pb-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Разрешения доступа</h2>
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">Выбрано: {form.permissions.length}</span>
              </div>
              <div className="grid max-h-[560px] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-2">
                {Object.entries(availablePages).map(([category, pages]) => (
                  <div key={category} className="rounded-lg border border-gray-200 p-3">
                    <div className="space-y-1">
                      {pages.map(page => (
                        <label key={page.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                          <input type="checkbox" checked={form.permissions.includes(page.id)} onChange={() => togglePermission(page.id)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <span>{page.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserEditorPage;
